import React, { useMemo, useEffect, useRef } from 'react';
import moment from 'moment';
import LazyImage from '../LazyImage';

const EMPTY_SEGMENTS = { leftSegments: [], rightSegments: [] };

function splitLines(text) {
  const raw = (text || '').toString();
  if (!raw) return [''];
  const lines = raw.split('\n');
  return lines.map((line, index) => (index < lines.length - 1 ? `${line}\n` : line));
}

function splitWords(text) {
  const raw = (text || '').toString();
  if (!raw) return [''];
  return raw.split(/(\s+)/).filter(token => token !== '');
}

function diffTokens(leftTokens, rightTokens) {
  const leftLength = leftTokens.length;
  const rightLength = rightTokens.length;
  const table = Array.from({ length: leftLength + 1 }, () => Array(rightLength + 1).fill(0));

  for (let i = leftLength - 1; i >= 0; i -= 1) {
    for (let j = rightLength - 1; j >= 0; j -= 1) {
      if (leftTokens[i] === rightTokens[j]) {
        table[i][j] = table[i + 1][j + 1] + 1;
      } else {
        table[i][j] = Math.max(table[i + 1][j], table[i][j + 1]);
      }
    }
  }

  const segments = [];
  const pushSegment = (type, value) => {
    if (!value) return;
    const last = segments[segments.length - 1];
    if (last && last.type === type) {
      last.value += value;
    } else {
      segments.push({ type, value });
    }
  };

  let i = 0;
  let j = 0;
  while (i < leftLength && j < rightLength) {
    if (leftTokens[i] === rightTokens[j]) {
      pushSegment('unchanged', leftTokens[i]);
      i += 1;
      j += 1;
    } else if (table[i + 1][j] >= table[i][j + 1]) {
      pushSegment('removed', leftTokens[i]);
      i += 1;
    } else {
      pushSegment('added', rightTokens[j]);
      j += 1;
    }
  }

  while (i < leftLength) {
    pushSegment('removed', leftTokens[i]);
    i += 1;
  }
  while (j < rightLength) {
    pushSegment('added', rightTokens[j]);
    j += 1;
  }

  return segments;
}

function buildDiffSegments(leftText, rightText, mode = 'lines') {
  const leftTokens = mode === 'words' ? splitWords(leftText) : splitLines(leftText);
  const rightTokens = mode === 'words' ? splitWords(rightText) : splitLines(rightText);
  const segments = diffTokens(leftTokens, rightTokens);
  const leftSegments = [];
  const rightSegments = [];

  segments.forEach((segment) => {
    if (segment.type === 'added') {
      rightSegments.push(segment);
      return;
    }
    if (segment.type === 'removed') {
      leftSegments.push(segment);
      return;
    }
    leftSegments.push(segment);
    rightSegments.push(segment);
  });

  return { leftSegments, rightSegments };
}

function renderSegments(segments) {
  return segments.map((segment, index) => (
    <span key={`${segment.type}-${index}`} className={`diff-${segment.type}`}>
      {segment.value}
    </span>
  ));
}

function normalizeImages(images) {
  if (!Array.isArray(images)) return [];
  return images.filter(Boolean);
}

function getYouTubeVideoID(url) {
  try {
    const parsedUrl = new URL(url);
    const videoID = parsedUrl.searchParams.get('v');
    if (videoID) return videoID;
    const pathnameSegments = parsedUrl.pathname.split('/');
    if (pathnameSegments[1] === 'shorts' && pathnameSegments.length > 2) {
      return pathnameSegments[2];
    }
    if (pathnameSegments.length > 1) {
      return pathnameSegments[1];
    }
    return null;
  } catch (e) {
    return null;
  }
}

function getVideoThumb(url) {
  const id = getYouTubeVideoID(url);
  if (!id) return null;
  return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
}

function resolveImageUrl(image) {
  if (!image) return null;
  if (image.type === 'video' && image.url) {
    return getVideoThumb(image.url) || image.url;
  }
  return image.url || null;
}

function resolveThumbnailUrl(url) {
  if (!url) return null;
  if (url.startsWith('https://www.youtube.com/') || url.startsWith('https://youtu.be/')) {
    return getVideoThumb(url) || url;
  }
  return url;
}

function buildImageDiff(leftImages, rightImages) {
  const leftList = normalizeImages(leftImages);
  const rightList = normalizeImages(rightImages);
  const rightUrls = new Set(rightList.map((img) => img?.url).filter(Boolean));
  const leftUrls = new Set(leftList.map((img) => img?.url).filter(Boolean));

  const leftDiff = leftList.map((img) => ({
    ...img,
    status: rightUrls.has(img?.url) ? 'unchanged' : 'removed'
  }));

  const rightDiff = rightList.map((img) => ({
    ...img,
    status: leftUrls.has(img?.url) ? 'unchanged' : 'added'
  }));

  return { leftDiff, rightDiff };
}

function stepSignature(step) {
  if (!step) return '';
  const images = Array.isArray(step.images) ? step.images.map(img => img?.url || '').filter(Boolean) : [];
  return JSON.stringify({
    title: step.title || '',
    content: step.content || '',
    images
  });
}

function buildStepPairs(leftSteps, rightSteps) {
  const leftList = Array.isArray(leftSteps) ? leftSteps : [];
  const rightList = Array.isArray(rightSteps) ? rightSteps : [];
  const leftById = new Map();
  const leftByPosition = new Map();
  leftList.forEach((step) => {
    if (!step) return;
    if (step.stepId) leftById.set(step.stepId, step);
    if (Number.isFinite(Number(step.position))) leftByPosition.set(Number(step.position), step);
  });

  const usedLeftIds = new Set();
  const usedLeftPositions = new Set();
  const pairs = [];

  rightList.forEach((rightStep) => {
    if (!rightStep) return;
    let leftStep = null;
    if (rightStep.stepId && leftById.has(rightStep.stepId)) {
      leftStep = leftById.get(rightStep.stepId);
      usedLeftIds.add(leftStep.stepId);
    } else if (Number.isFinite(Number(rightStep.position)) && leftByPosition.has(Number(rightStep.position))) {
      const candidate = leftByPosition.get(Number(rightStep.position));
      if (candidate && !usedLeftPositions.has(Number(candidate.position))) {
        leftStep = candidate;
        usedLeftPositions.add(Number(candidate.position));
        if (candidate.stepId) usedLeftIds.add(candidate.stepId);
      }
    }
    pairs.push({ leftStep, rightStep });
  });

  leftList.forEach((leftStep) => {
    if (!leftStep) return;
    if (leftStep.stepId && usedLeftIds.has(leftStep.stepId)) return;
    if (Number.isFinite(Number(leftStep.position)) && usedLeftPositions.has(Number(leftStep.position))) return;
    pairs.push({ leftStep, rightStep: null });
  });

  return pairs;
}

function buildStepDiff(leftStep, rightStep) {
  const titleDiff = buildDiffSegments(leftStep?.title || '', rightStep?.title || '', 'words');
  const contentDiff = buildDiffSegments(leftStep?.content || '', rightStep?.content || '', 'lines');
  const imageDiff = buildImageDiff(leftStep?.images, rightStep?.images);
  let status = 'unchanged';
  if (leftStep && !rightStep) status = 'removed';
  if (!leftStep && rightStep) status = 'added';
  if (leftStep && rightStep && stepSignature(leftStep) !== stepSignature(rightStep)) status = 'modified';
  return { leftStep, rightStep, titleDiff, contentDiff, imageDiff, status };
}

function StepDiffCard({ step, diff, side }) {
  if (!step) return null;
  const isLeft = side === 'left';
  const segments = isLeft ? diff.titleDiff.leftSegments : diff.titleDiff.rightSegments;
  const contentSegments = isLeft ? diff.contentDiff.leftSegments : diff.contentDiff.rightSegments;
  const images = isLeft ? diff.imageDiff.leftDiff : diff.imageDiff.rightDiff;
  const statusClass = diff.status === 'added'
    ? (isLeft ? '' : 'history-step-added')
    : diff.status === 'removed'
      ? (isLeft ? 'history-step-removed' : '')
      : diff.status === 'modified'
        ? 'history-step-modified'
        : '';

  return (
    <div className={`history-step-card ${statusClass}`}>
      <h4 className="history-step-title">
        Step {step.position}: <span className="history-step-title-text">{renderSegments(segments)}</span>
      </h4>
      <div className="history-step-images">
        {images.length ? images.map((img, index) => (
          <div key={`${img?.url || index}`} className={`history-image-frame ${img.status}`}>
            {img?.url ? (
              <LazyImage
                alt={`Step ${step.position} image`}
                src={resolveImageUrl(img)}
                className="history-image"
                placeholderStyles={{ minHeight: 80 }}
              />
            ) : (
              <div className="history-image-placeholder" />
            )}
          </div>
        )) : (
          <div className="history-empty">No images.</div>
        )}
      </div>
      <div className="history-text history-step-content">{renderSegments(contentSegments)}</div>
    </div>
  );
}

function GuideSide({ guide, diff, side }) {
  if (!guide) {
    return <div className="history-empty">No data.</div>;
  }
  const isLeft = side === 'left';
  const titleSegments = isLeft ? diff.title.leftSegments : diff.title.rightSegments;
  const descSegments = isLeft ? diff.description.leftSegments : diff.description.rightSegments;
  const needSegments = isLeft ? diff.whatyouneed.leftSegments : diff.whatyouneed.rightSegments;
  const mainImages = isLeft ? diff.mainImage.leftDiff : diff.mainImage.rightDiff;
  const steps = diff.steps
    .filter(stepDiff => (isLeft ? stepDiff.leftStep : stepDiff.rightStep))
    .sort((a, b) => {
      const aStep = isLeft ? a.leftStep : a.rightStep;
      const bStep = isLeft ? b.leftStep : b.rightStep;
      return Number(aStep?.position || 0) - Number(bStep?.position || 0);
    });

  return (
    <>
      <h2 className="history-guide-title">{renderSegments(titleSegments)}</h2>
      <div className="history-guide-images">
        {mainImages.length ? mainImages.map((img, index) => (
          <div key={`${img?.url || index}`} className={`history-image-frame ${img.status}`}>
            {img?.url ? (
              <LazyImage alt="Guide main" src={resolveImageUrl(img)} className="history-image" placeholderStyles={{ minHeight: 120 }} />
            ) : (
              <div className="history-image-placeholder" />
            )}
          </div>
        )) : null}
      </div>
      {(guide.description || '').length > 0 && (
        <div className="history-section">
          <h4>Description</h4>
          <div className="history-text">{renderSegments(descSegments)}</div>
        </div>
      )}
      {(guide.whatyouneed || '').length > 0 && (
        <div className="history-section">
          <h4>What you need</h4>
          <div className="history-text">{renderSegments(needSegments)}</div>
        </div>
      )}
      <div className="history-section">
        {steps.map((stepDiff, index) => (
          <StepDiffCard
            key={stepDiff.leftStep?.stepId || stepDiff.rightStep?.stepId || `step-${index}`}
            step={isLeft ? stepDiff.leftStep : stepDiff.rightStep}
            diff={stepDiff}
            side={side}
          />
        ))}
      </div>
    </>
  );
}

function StepSide({ step, diff, side }) {
  if (!step) {
    return <div className="history-empty">Select a revision to compare.</div>;
  }
  return <StepDiffCard step={step} diff={diff} side={side} />;
}

function HistoryTimeline({ entries, selectedRev, onSelect, mode }) {
  const trackRef = useRef(null);
  const stepSignatures = mode === 'step'
    ? entries.map(entry => stepSignature(entry?.step))
    : [];
  const guideSignatures = mode === 'guide'
    ? entries.map(entry => (entry?.signature || ''))
    : [];

  useEffect(() => {
    if (!entries.length) return;
    const track = trackRef.current;
    if (!track) return;
    const newest = entries[entries.length - 1];
    if (newest && selectedRev === newest.rev) {
      track.scrollLeft = track.scrollWidth;
    }
  }, [entries, selectedRev]);
  return (
    <div className="history-timeline">
      <div className="history-timeline-track" ref={trackRef}>
        {entries.map((entry, index) => (
          (() => {
            const prevStepSignature = stepSignatures[index - 1];
            const prevGuideSignature = guideSignatures[index - 1];
            const noChange = mode === 'step'
              ? (prevStepSignature && stepSignatures[index] === prevStepSignature)
              : (prevGuideSignature && guideSignatures[index] === prevGuideSignature);
            return (
          <button
            key={entry.rev}
            type="button"
            className={`history-rev-card${selectedRev === entry.rev ? ' active' : ''}${noChange ? ' no-change' : ''}`}
            onClick={() => onSelect(entry.rev)}
          >
            <div className="history-rev-thumb">
              {entry.thumbnailUrl ? (
                <LazyImage
                  alt={`Revision ${entry.rev}`}
                  src={resolveThumbnailUrl(entry.thumbnailUrl)}
                  className="history-rev-thumb-image"
                  placeholderStyles={{ minHeight: 70 }}
                />
              ) : (
                <div className="history-rev-thumb-placeholder" />
              )}
            </div>
            <div className="history-rev-meta">
              <div className="history-rev-label">Rev {entry.rev}</div>
              <div className="history-rev-date">
                {entry.updateDate ? moment(entry.updateDate).fromNow() : 'Unknown'}
              </div>
            </div>
          </button>
            );
          })()
        ))}
      </div>
    </div>
  );
}

export default function HistoryViewer({
  mode,
  currentGuide,
  currentStep,
  historyEntries,
  selectedRevision,
  selectedRev,
  onSelectRevision,
  onRestoreRevision,
  onClose,
  loading
}) {
  const leftGuide = mode === 'guide' ? selectedRevision : null;
  const rightGuide = mode === 'guide' ? currentGuide : null;
  const leftStep = mode === 'step' ? selectedRevision : null;
  const rightStep = mode === 'step' ? currentStep : null;

  const guideDiff = useMemo(() => {
    if (mode !== 'guide') return null;
    const title = buildDiffSegments(leftGuide?.title || '', rightGuide?.title || '', 'words');
    const description = buildDiffSegments(leftGuide?.description || '', rightGuide?.description || '', 'lines');
    const whatyouneed = buildDiffSegments(leftGuide?.whatyouneed || '', rightGuide?.whatyouneed || '', 'lines');
    const mainImage = buildImageDiff(
      leftGuide?.mainImage ? [leftGuide.mainImage] : [],
      rightGuide?.mainImage ? [rightGuide.mainImage] : []
    );
    const stepPairs = buildStepPairs(leftGuide?.steps || [], rightGuide?.steps || []);
    const steps = stepPairs.map(pair => buildStepDiff(pair.leftStep, pair.rightStep));
    return { title, description, whatyouneed, mainImage, steps };
  }, [mode, leftGuide, rightGuide]);

  const stepDiff = useMemo(() => {
    if (mode !== 'step') return null;
    if (!leftStep && !rightStep) return null;
    return buildStepDiff(leftStep, rightStep);
  }, [mode, leftStep, rightStep]);

  const currentRev = currentGuide?.rev;
  const timelineEntries = useMemo(() => {
    if (!Array.isArray(historyEntries)) return [];
    return [...historyEntries].reverse();
  }, [historyEntries]);
  const headerLabel = mode === 'guide' ? 'Guide history' : 'Step history';
  const compareLabel = selectedRev
    ? `Comparing revision ${selectedRev} with current${currentRev ? ` (rev ${currentRev})` : ''}`
    : 'Select a revision to compare';
  const restoreLabel = mode === 'guide' ? 'Restore guide revision' : 'Restore step revision';
  const previousLabel = selectedRev ? `Previous (rev ${selectedRev})` : 'Previous';
  const currentLabel = currentRev ? `Current (rev ${currentRev})` : 'Current';

  return (
    <div className="history-mode">
      <div className="history-toolbar">
        <div>
          <h3>{headerLabel}</h3>
          <div className="history-compare">{compareLabel}</div>
        </div>
        <div className="history-actions">
          <button
            type="button"
            className="btn btn-sm btn-outline-dark"
            onClick={() => onRestoreRevision && selectedRev && onRestoreRevision(selectedRev)}
            disabled={!selectedRev || loading}
          >
            {restoreLabel}
          </button>
          <button type="button" className="btn btn-sm btn-outline-dark" onClick={onClose}>
            Exit history
          </button>
        </div>
      </div>
      <div className="history-main">
        <div className="history-diff">
          <div className="history-pane history-pane-left">
            <div className="history-pane-label">{previousLabel}</div>
            {mode === 'guide' ? (
              <GuideSide guide={leftGuide} diff={guideDiff || { title: EMPTY_SEGMENTS, description: EMPTY_SEGMENTS, whatyouneed: EMPTY_SEGMENTS, mainImage: { leftDiff: [], rightDiff: [] }, steps: [] }} side="left" />
            ) : (
              <StepSide step={leftStep} diff={stepDiff || buildStepDiff(leftStep, rightStep)} side="left" />
            )}
          </div>
          <div className="history-pane history-pane-right">
            <div className="history-pane-label">{currentLabel}</div>
            {mode === 'guide' ? (
              <GuideSide guide={rightGuide} diff={guideDiff || { title: EMPTY_SEGMENTS, description: EMPTY_SEGMENTS, whatyouneed: EMPTY_SEGMENTS, mainImage: { leftDiff: [], rightDiff: [] }, steps: [] }} side="right" />
            ) : (
              <StepSide step={rightStep} diff={stepDiff || buildStepDiff(leftStep, rightStep)} side="right" />
            )}
          </div>
        </div>
      </div>
      <HistoryTimeline entries={timelineEntries} selectedRev={selectedRev} onSelect={onSelectRevision} mode={mode} />
    </div>
  );
}
